import { Model } from './model';
import { Type } from 'class-transformer';
import { Partner } from './partner';
import { Deduction } from './deduction';
import { ClaimLineItem } from './claim_line_item';
import { TransformStringToDayJS } from './utils';
import dayjs from 'dayjs';
import { Attachment } from './attachment';
import { DeductionDocument } from './deduction_document';
import { DeductionReason } from './deduction_reason';
import { sumArray } from '@stuut/common/src/utils';

class Document extends Model {
  id: string;
  status: string;
  type: string;

  @Type(() => Attachment)
  attachment: Attachment;
}

export class Claim extends Model {
  id: string;

  amount: number;

  category: string;

  currency: string;

  external_reason: string;

  external_reason_code: string;

  external_reason_comments: string;

  name: string;

  notes: string | null;

  po_number: string;

  purchase_order_numbers: string[];

  deal_numbers: string[];

  reason_id: string;

  reference: string;

  is_aggregated: boolean;

  @TransformStringToDayJS()
  date: dayjs.Dayjs | null;

  @Type(() => Deduction)
  deduction: Deduction;

  @Type(() => DeductionDocument)
  document: DeductionDocument;

  @Type(() => ClaimLineItem)
  line_items: ClaimLineItem[];

  @Type(() => Partner)
  partner: Partner | null;

  @Type(() => DeductionReason)
  reason: DeductionReason | null;

  meta?: Record<string, any>;

  get approved() {
    return this.line_items?.every((item) => item.approved);
  }

  get line_items_sum() {
    return sumArray(
      this.line_items?.map((item) => {
        const amount = item?.total_amount;

        if (!amount) return 0;

        const num = typeof amount === 'string' ? parseFloat(amount) : Number(amount);
        return isNaN(num) ? 0 : num;
      }),
    ) as number;
  }

  get amount_diff() {
    return Number(this.amount) - this.line_items_sum;
  }

  get amount_mismatch() {
    return Math.round(this.line_items_sum * 100) !== Math.round(Number(this.amount) * 100);
  }

  get approvedCount() {
    return this.line_items?.filter((item) => item.approved)?.length;
  }

  get rejectedCount() {
    return this.line_items?.filter((item) => item.rejected)?.length;
  }

  get pendingCount() {
    return this.line_items?.filter((item) => item.pending)?.length;
  }

  get deduction_conflict() {
    return Number(this.deduction?.amount) && Number(this.amount) !== Number(this.deduction?.amount);
  }

  get deduction_conflict_amount() {
    return Number(this.amount) - Number(this.deduction?.amount);
  }
}
